import {
  DEFAULT_INVALIDATE_OPTIONS,
  DEFAULT_QUERY_OPTIONS,
  UseMutationCallbackType,
} from "#hooks/adapters/adapterUtils";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useToast } from "@validereinc/common-components";
import {
  CompanyUnitConfigurationAdapter,
  CreateOneRequestType,
  DeleteOneRequestType,
  UnitConfigurationType,
  UpdateOneRequestType,
} from "@validereinc/domain";

export const COMPANY_UNIT_CONFIG_KEY = "companyUnitConfig";

export const useListCompanyUnitConfig = (
  params: Parameters<typeof CompanyUnitConfigurationAdapter.getList>[0] = {},
  options: { enabled?: boolean } = {}
) =>
  useQuery({
    queryKey: [COMPANY_UNIT_CONFIG_KEY, params],
    queryFn: () => CompanyUnitConfigurationAdapter.getList(params),
    ...DEFAULT_QUERY_OPTIONS,
    ...options,
  });

export const useCompanyUnitConfigCache = () => {
  const queryClient = useQueryClient();
  return {
    invalidate: () => {
      queryClient.invalidateQueries({
        queryKey: [COMPANY_UNIT_CONFIG_KEY],
        ...DEFAULT_INVALIDATE_OPTIONS,
      });
    },
  };
};

export const useUpdateCompanyUnitConfig = ({
  onSuccess,
}: UseMutationCallbackType<
  Awaited<ReturnType<typeof CompanyUnitConfigurationAdapter.updateOne>>
> = {}) => {
  const { toast } = useToast();
  const { invalidate } = useCompanyUnitConfigCache();

  return useMutation({
    mutationFn: async (params: UpdateOneRequestType<UnitConfigurationType>) =>
      CompanyUnitConfigurationAdapter.updateOne(params),
    onSuccess: (data, variables, context) => {
      toast.push({
        intent: "success",
        description: "Successfully updated unit preference.",
      });
      invalidate();
      onSuccess?.(data, variables, context);
    },
    onError: () => {
      toast.push({
        intent: "error",
        description: "Failed to update unit preference.",
      });
    },
  });
};

export const useCreateCompanyUnitConfig = ({
  onSuccess,
}: UseMutationCallbackType<
  Awaited<ReturnType<typeof CompanyUnitConfigurationAdapter.createOne>>
> = {}) => {
  const { toast } = useToast();
  const { invalidate } = useCompanyUnitConfigCache();

  return useMutation({
    mutationFn: async (params: CreateOneRequestType<UnitConfigurationType>) =>
      CompanyUnitConfigurationAdapter.createOne(params),
    onSuccess: (data, variables, context) => {
      toast.push({
        intent: "success",
        description: "Successfully added unit preference.",
      });
      invalidate();
      onSuccess?.(data, variables, context);
    },
    onError: () => {
      toast.push({
        intent: "error",
        description: "Failed to add unit preference.",
      });
    },
  });
};

export const useDeleteCompanyUnitConfig = ({
  onSuccess,
}: UseMutationCallbackType<
  Awaited<ReturnType<typeof CompanyUnitConfigurationAdapter.deleteOne>>
> = {}) => {
  const { toast } = useToast();
  const { invalidate } = useCompanyUnitConfigCache();

  return useMutation({
    mutationFn: async (id: DeleteOneRequestType<UnitConfigurationType>) =>
      CompanyUnitConfigurationAdapter.deleteOne(id),
    onSuccess: (data, variables, context) => {
      toast.push({
        intent: "success",
        description: `Unit Preference has been successfully deleted.`,
      });
      invalidate();
      onSuccess?.(data, variables, context);
    },
  });
};
